import {
	Dialog,
	DialogTitle,
	DialogContent,
	DialogActions,
	Button,
	FormControl,
	InputLabel,
	MenuItem,
	Select,
	Typography,
	Box,
	Tooltip,
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { DateTimeRangePicker } from '@mui/x-date-pickers-pro/DateTimeRangePicker';
import dayjs from 'dayjs';

import PropTypes from 'prop-types';
import { useState, useEffect, useMemo, useCallback } from 'react';

const EmailJobConfigModal = ({
	lastSyncEndMicrosoft,
	lastSyncEndGoogle,
	open,
	onClose,
	onConfirm,
	loading,
	error, // stores error returned from BE after user hits confirm
	setError,
	availableProviders = [],
}) => {
	// Controlled form state for the modal
	const [provider, setProvider] = useState(availableProviders[0] || '');
	const [timeRange, setTimeRange] = useState([
		dayjs().subtract(7, 'days').startOf('minutes'),
		dayjs(),
	]); // array [start, end]
	const [timeRangeErrors, setTimeRangeErrors] = useState([]); // array of [startError, endError], real-time update
	const [errorMessage, setErrorMessage] = useState(error); // only updates when user closes the picker

	const hasTimeRangeError = useMemo(
		() => timeRangeErrors.some((e) => e != null),
		[timeRangeErrors] // Recalculate when timeRangeErrors changes
	);

	// confirm button is disabled if there is a timeRangeError
	const handleSubmit = () => {
		// Converts to ISO 8601
		const range_start = timeRange[0].toISOString();
		const range_end = timeRange[1].toISOString();

		onConfirm(provider, range_start, range_end);
	};

	const clearError = () => {
		setErrorMessage('');
		setError(null);
	};

	const handleErrorMessage = () => {
		hasTimeRangeError
			? setErrorMessage('Please select a valid time range.')
			: setErrorMessage('');
	};

	// Helper function to calculate sync time range for a provider
	const calculateSyncTimeRange = useCallback(
		(provider) => {
			const lastSyncEnd =
				provider === 'microsoft' ? lastSyncEndMicrosoft : lastSyncEndGoogle;
			const syncStartMin = lastSyncEnd
				? dayjs(lastSyncEnd).add(1, 'minute').startOf('minute')
				: dayjs().subtract(7, 'days').startOf('minutes');
			return [syncStartMin, dayjs()];
		},
		[lastSyncEndMicrosoft, lastSyncEndGoogle]
	);

	// Update time range when provider changes
	useEffect(() => {
		setTimeRange(calculateSyncTimeRange(provider));
	}, [provider, calculateSyncTimeRange]);

	useEffect(() => {
		// reset values when modal opens
		if (open) {
			const defaultProvider = availableProviders[0] || '';
			setProvider(defaultProvider);
			setTimeRange(calculateSyncTimeRange(defaultProvider));
			setErrorMessage('');
		}
	}, [open, calculateSyncTimeRange, availableProviders]);

	return (
		<Dialog open={open} onClose={onClose} maxWidth='md' fullWidth>
			<DialogTitle>New Job Configuration</DialogTitle>
			<DialogContent dividers sx={{ pt: 3 }}>
				<FormControl sx={{ minWidth: 200, mr: 2, mb: 4 }}>
					<InputLabel id='email-provider-label'>Provider</InputLabel>
					<Select
						labelId='email-provider-label'
						value={provider}
						label='Email Provider'
						onChange={(e) => setProvider(e.target.value)}
					>
						{availableProviders.includes('microsoft') && (
							<MenuItem value='microsoft'>Microsoft Outlook</MenuItem>
						)}
						{availableProviders.includes('google') && (
							<MenuItem value='google'>Gmail</MenuItem>
						)}
					</Select>
				</FormControl>

				<Tooltip
					title='The sync time range is defaulted to start from the last successful sync for the selected provider, or 7 days ago if no previous sync exists.'
					placement='top'
					arrow
				>
					<span>
						<DateTimeRangePicker
							value={timeRange}
							onChange={(newValue) => setTimeRange(newValue)}
							localeText={{ start: 'Start Sync', end: 'End Sync' }}
							timeSteps={{ minutes: 1 }}
							minDateTime={dayjs().subtract(7, 'days').startOf('minutes')}
							disableFuture
							onError={(error) => setTimeRangeErrors(error)}
							onOpen={clearError}
							onClose={handleErrorMessage}
							closeOnSelect={false}
						/>
					</span>
				</Tooltip>

				<Box sx={{ mt: 2, height: '20px', width: '100%' }}>
					<Typography sx={{ textAlign: 'end' }} color='error'>
						{errorMessage} {error}
					</Typography>
				</Box>
			</DialogContent>
			<DialogActions sx={{ p: 2 }}>
				<Button
					onClick={onClose}
					variant='outlined'
					size='medium'
					sx={{ mr: 1 }}
				>
					Cancel
				</Button>

				<LoadingButton
					variant='outlined'
					onClick={handleSubmit}
					loading={loading}
					disabled={loading || hasTimeRangeError}
					size='medium'
				>
					Confirm
				</LoadingButton>
			</DialogActions>
		</Dialog>
	);
};

EmailJobConfigModal.propTypes = {
	lastSyncEndMicrosoft: PropTypes.object,
	lastSyncEndGoogle: PropTypes.object,
	open: PropTypes.bool.isRequired,
	onClose: PropTypes.func.isRequired,
	onConfirm: PropTypes.func.isRequired,
	loading: PropTypes.bool,
	error: PropTypes.string,
	setError: PropTypes.func,
	availableProviders: PropTypes.arrayOf(PropTypes.string),
};

export default EmailJobConfigModal;
